<template>
  <div class="intro-page">
    <section class="section">
      <div class="container is-fluid intro-container">

      
        <!-- Başlık -->
        <div class="page-head">
          <h1 class="title is-4">{{ $t('intro.title') }}</h1>
          <p class="page-sub">{{ $t('intro.subtitle') }}</p>
        </div>

        <!-- Adım şeridi -->
        <div class="step-strip">
          <div v-for="(step, i) in steps" :key="i"
               class="step-dot"
               :class="{ 'is-active': i === current, 'is-done': i < current }"
               @click="goTo(i)">
            <span class="dot">{{ i + 1 }}</span>
            <span class="dot-label">{{ step.short }}</span>
          </div>
        </div>

        <!-- Sahne -->
        <div class="stage">
          <div class="columns is-vcentered">

            <!-- Görsel -->
            <div class="column is-6">
              <div class="art">
                <svg viewBox="0 0 320 240" width="100%" preserveAspectRatio="xMidYMid meet">

                  <!-- 1. Belgeyi hazırla -->
                  <g v-if="current === 0">
                    <rect class="paper" x="96" y="34" width="128" height="168" rx="6" />
                    <rect class="line" x="116" y="62" width="88" height="7" rx="3.5" />
                    <rect class="line-soft" x="116" y="82" width="70" height="6" rx="3" />
                    <rect class="line-soft" x="116" y="98" width="84" height="6" rx="3" />
                    <rect class="line-soft" x="116" y="114" width="60" height="6" rx="3" />
                    <g class="pop">
                      <circle class="accent-fill" cx="214" cy="188" r="22" />
                      <path class="on-accent" d="M214 178 v20 M206 186 l8 -8 l8 8" />
                    </g>
                    <rect class="card-ghost" x="66" y="48" width="24" height="140" rx="5" />
                    <rect class="card-ghost" x="38" y="62" width="20" height="112" rx="5" />
                  </g>

                  <!-- 2. Alanları yerleştir -->
                  <g v-if="current === 1">
                    <rect class="paper" x="80" y="28" width="160" height="184" rx="6" />
                    <rect class="line-soft" x="100" y="52" width="100" height="6" rx="3" />
                    <rect class="line-soft" x="100" y="68" width="120" height="6" rx="3" />
                    <rect class="line-soft" x="100" y="84" width="84" height="6" rx="3" />
                    <rect class="field-box" x="100" y="118" width="82" height="18" rx="3" />
                    <rect class="sign-box" x="100" y="150" width="98" height="40" rx="4" />
                    <path class="squiggle-hint" d="M112 176 q14 -18 26 -2 q12 16 26 -8" />
                    <g class="cursor">
                      <path class="pointer" d="M196 158 l0 26 l7 -7 l5 11 l6 -3 l-5 -11 l10 0 z" />
                    </g>
                  </g>

                  <!-- 3. Alıcıları gir -->
                  <g v-if="current === 2">
                    <g v-for="(row, n) in [0, 1]" :key="n" :transform="'translate(0,' + (n * 74) + ')'">
                      <rect class="paper" x="52" y="52" width="216" height="56" rx="6" />
                      <circle class="avatar" cx="82" cy="80" r="15" />
                      <path class="avatar-mark" d="M82 76 a5 5 0 1 0 0.1 0 M71 93 a11 9 0 0 1 22 0" />
                      <rect class="line" x="110" y="68" width="62" height="7" rx="3.5" />
                      <rect class="line-soft" x="110" y="84" width="102" height="6" rx="3" />
                      <text class="badge-num" x="246" y="85">{{ n + 1 }}</text>
                    </g>
                  </g>

                  <!-- 4. Gönder -->
                  <g v-if="current === 3">
                    <rect class="paper" x="26" y="82" width="86" height="62" rx="6" />
                    <path class="envelope-flap" d="M26 88 l43 34 l43 -34" />
                    <path class="flight" d="M120 112 q52 -46 104 -34" />
                    <path class="flight" d="M120 118 q52 42 104 30" />
                    <g>
                      <circle class="accent-soft" cx="246" cy="70" r="24" />
                      <path class="avatar-mark" d="M246 65 a6 6 0 1 0 0.1 0 M233 84 a13 11 0 0 1 26 0" />
                    </g>
                    <g>
                      <circle class="accent-soft" cx="246" cy="164" r="24" />
                      <path class="avatar-mark" d="M246 159 a6 6 0 1 0 0.1 0 M233 178 a13 11 0 0 1 26 0" />
                    </g>
                  </g>

                  <!-- 5. İmzala -->
                  <g v-if="current === 4">
                    <rect class="paper" x="60" y="44" width="200" height="152" rx="6" />
                    <rect class="line-soft" x="82" y="68" width="120" height="6" rx="3" />
                    <rect class="line-soft" x="82" y="84" width="90" height="6" rx="3" />
                    <rect class="sign-box" x="82" y="110" width="156" height="56" rx="4" />
                    <path class="squiggle" d="M98 146 q18 -30 34 -6 q10 16 20 -10 q10 -24 24 6 q8 20 22 -10" />
                    <rect class="check-box" x="82" y="176" width="12" height="12" rx="2" />
                    <path class="check-mark" d="M85 182 l3 3 l6 -7" />
                    <rect class="line-soft" x="100" y="179" width="86" height="6" rx="3" />
                  </g>

                  <!-- 6. Mühürle -->
                  <g v-if="current === 5">
                    <rect class="paper" x="70" y="30" width="150" height="180" rx="6" />
                    <rect class="line-soft" x="90" y="54" width="100" height="6" rx="3" />
                    <rect class="line-soft" x="90" y="70" width="82" height="6" rx="3" />
                    <path class="squiggle" d="M92 116 q14 -22 26 -4 q8 12 16 -8" />
                    <g class="pop">
                      <circle class="seal" cx="206" cy="176" r="30" />
                      <path class="on-accent" d="M198 174 v-6 a8 8 0 0 1 16 0 v6" />
                      <rect class="on-accent-fill" x="196" y="174" width="20" height="16" rx="3" />
                    </g>
                    <g class="clock">
                      <circle class="clock-face" cx="94" cy="176" r="20" />
                      <path class="clock-hand" d="M94 176 v-11 M94 176 l8 6" />
                    </g>
                    <path class="chain" d="M126 200 h54" />
                  </g>

                  <!-- 7. Doğrula -->
                  <g v-if="current === 6">
                    <rect class="paper" x="72" y="34" width="148" height="172" rx="6" />
                    <rect class="line-soft" x="92" y="58" width="96" height="6" rx="3" />
                    <rect class="line-soft" x="92" y="74" width="76" height="6" rx="3" />
                    <rect class="code-chip" x="92" y="104" width="108" height="22" rx="4" />
                    <text class="code-text" x="146" y="119">A7K2-9MPX</text>
                    <g class="pop">
                      <circle class="glass" cx="206" cy="168" r="34" />
                      <path class="glass-handle" d="M230 192 l22 22" />
                      <path class="check-big" d="M192 168 l10 11 l20 -24" />
                    </g>
                  </g>
                </svg>
              </div>
            </div>

            <!-- Metin -->
            <div class="column is-6">
              <div class="stage-text">
              <p class="step-index">{{ $t('intro.stepWord') }} {{ current + 1 }} / {{ steps.length }}</p>
              <h2 class="title is-4">{{ step.title }}</h2>
              <p class="step-text">{{ step.text }}</p>

              <ul class="step-points">
                <li v-for="(point, i) in step.points" :key="i">
                  <span class="icon"><i class="fa fa-check"></i></span>
                  <span>{{ point }}</span>
                </li>
              </ul>

              <div class="buttons mt-5">
                <button class="button" :disabled="current === 0" @click="goTo(current - 1)">
                  <span class="icon"><i class="fa fa-arrow-left"></i></span>
                  <span>{{ $t('intro.prev') }}</span>
                </button>
                <button class="button is-link" v-if="current < steps.length - 1"
                        @click="goTo(current + 1)">
                  <span>{{ $t('intro.next') }}</span>
                  <span class="icon"><i class="fa fa-arrow-right"></i></span>
                </button>
                <button class="button is-link is-light" v-else @click="goTo(0)">
                  <span class="icon"><i class="fa fa-repeat"></i></span>
                  <span>{{ $t('intro.restart') }}</span>
                </button>
              </div>
              </div>
            </div>
          </div>

          <!-- İlerleme -->
          <div class="progress-rail">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
        </div>

        <p class="has-text-centered has-text-grey is-size-7 mt-5">{{ $t('intro.footnote') }}</p>
      </div>
    </section>
  </div>
</template>

<script>


export default {
  name: 'Intro',
  components: {  },
  props: {
    // Otomatik ilerleme. Kullanıcı bir adıma dokunduğu anda duruyor —
    // okurken altından kayan bir sunum sinir bozucu oluyor.
    autoplay: { type: Boolean, default: true },
    interval: { type: Number, default: 6000 }
  },
  data() {
    return {
      current: 0,
      timer: null
    };
  },
  computed: {
    // Diziyi her kullanımda $tm ile çekmek yerine tek yerde: dil değişince
    // observable üzerinden kendiliğinden yeniden hesaplanıyor
    steps() {
      return this.$tm('intro.steps');
    },
    // Dil değişince adım sayısı aynı kalıyor ama yine de sınırı koruyoruz
    step() {
      return this.steps[Math.min(this.current, this.steps.length - 1)] || {};
    },
    progress() {
      return ((this.current + 1) / this.steps.length) * 100;
    }
  },
  mounted() {
    if (this.autoplay) {
      this.timer = setInterval(() => {
        this.current = (this.current + 1) % this.steps.length;
      }, this.interval);
    }
  },
  beforeDestroy() {
    // Bırakılmayan interval, bileşen kapandıktan sonra da çalışmaya devam eder
    clearInterval(this.timer);
  },
  methods: {
    goTo(index) {
      clearInterval(this.timer);
      this.timer = null;
      var count = this.steps.length;
      this.current = ((index % count) + count) % count;
    }
  }
}
</script>

<style scoped>
.intro-page {
  background: #f6f7fb;
  min-height: 100vh;
}

.intro-container {
  padding: 0 3rem;
}

/* Sahne tam genislige yayiliyor ama metin sutunu okunabilir kaliyor:
   1400 piksel genisliginde tek satir goz icin cok uzun. */
.stage-text {
  max-width: 560px;
}

.art svg {
  display: block;
  width: 100%;
  height: clamp(240px, 26vw, 380px);
}

.lang-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.page-head {
  text-align: center;
  margin-bottom: 2rem;
}

/* Bulma, .title'i izleyen .subtitle'a negatif ust bosluk veriyor ve
   ikisi ust uste biniyor. Bu yuzden .subtitle sinifini hic kullanmiyoruz. */
.page-head .title {
  margin-bottom: 0.5rem;
}

.page-sub {
  color: #7a8199;
  font-size: 1.05rem;
  margin: 0;
}

/* --- Adım şeridi --- */
.step-strip {
  display: flex;
  gap: 4px;
  margin: 1.5rem 0;
  flex-wrap: wrap;
}

.step-dot {
  flex: 1 1 90px;
  text-align: center;
  cursor: pointer;
  padding: 6px 2px;
  border-radius: 6px;
  transition: background-color 0.15s;
}

.step-dot:hover { background: #eceef6; }

.step-dot .dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #dfe3ee;
  color: #6b7490;
  font-size: 12px;
  font-weight: 700;
  transition: background-color 0.2s, color 0.2s;
}

.step-dot.is-done .dot { background: #c5cee6; color: #3a4468; }
.step-dot.is-active .dot { background: #242441; color: #fff; }

.dot-label {
  display: block;
  font-size: 11px;
  color: #8a90a6;
  margin-top: 5px;
  letter-spacing: 0.2px;
}

.step-dot.is-active .dot-label { color: #242441; font-weight: 600; }

/* --- Sahne --- */
.stage {
  background: #fff;
  border-radius: 10px;
  padding: 2rem 2rem 0;
  box-shadow: 0 2px 10px rgba(36, 36, 65, 0.07);
  overflow: hidden;
}

.art {
  background: #f6f7fb;
  border-radius: 8px;
  padding: 1rem;
}

.step-index {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #9aa0b5;
  text-transform: uppercase;
  margin-bottom: 0.4rem;
}

.step-text {
  color: #4a4a4a;
  line-height: 1.65;
}

.step-points {
  margin-top: 1.2rem;
}

.step-points li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 14px;
  color: #5a6078;
}

.step-points .icon {
  color: #48c78e;
  font-size: 12px;
  margin-top: 3px;
}

.progress-rail {
  height: 3px;
  background: #eceef6;
  margin: 2rem -2rem 0;
}

.progress-fill {
  height: 100%;
  background: #242441;
  transition: width 0.4s ease;
}

/* --- Çizim öğeleri --- */
.paper { fill: #fff; stroke: #d8dce9; stroke-width: 1.5; }
.card-ghost { fill: #e8ebf5; }
.line { fill: #c3c9dc; }
.line-soft { fill: #e2e6f0; }

.field-box { fill: #eaf1fd; stroke: #7aa3e8; stroke-width: 1.2; }
.sign-box { fill: #eef3fc; stroke: #3273dc; stroke-width: 1.4; stroke-dasharray: 5 4; }
.squiggle { fill: none; stroke: #242441; stroke-width: 2.6; stroke-linecap: round; }
.squiggle-hint { fill: none; stroke: #b9c6e4; stroke-width: 2; stroke-linecap: round; }

.accent-fill { fill: #3273dc; }
.accent-soft { fill: #e3ecfb; }
.on-accent { fill: none; stroke: #fff; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
.on-accent-fill { fill: #fff; }

.avatar { fill: #e3ecfb; }
.avatar-mark { fill: none; stroke: #3273dc; stroke-width: 1.8; }
.badge-num { fill: #9aa0b5; font-size: 13px; font-weight: 700; text-anchor: middle; }

.envelope-flap { fill: none; stroke: #d8dce9; stroke-width: 1.5; }
.flight { fill: none; stroke: #b9c6e4; stroke-width: 1.6; stroke-dasharray: 4 5; }

.check-box { fill: #fff; stroke: #3273dc; stroke-width: 1.4; }
.check-mark { fill: none; stroke: #3273dc; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }

.seal { fill: #242441; }
.clock-face { fill: #fff; stroke: #9aa0b5; stroke-width: 1.8; }
.clock-hand { fill: none; stroke: #9aa0b5; stroke-width: 2; stroke-linecap: round; }
.chain { stroke: #c3c9dc; stroke-width: 2; stroke-dasharray: 3 4; }

.code-chip { fill: #f2f4fa; stroke: #c3c9dc; stroke-width: 1; }
.code-text { fill: #5a6078; font-size: 11px; font-weight: 700; text-anchor: middle; letter-spacing: 0.5px; }
.glass { fill: rgba(72, 199, 142, 0.12); stroke: #48c78e; stroke-width: 2.4; }
.glass-handle { stroke: #48c78e; stroke-width: 5; stroke-linecap: round; }
.check-big { fill: none; stroke: #48c78e; stroke-width: 3.4; stroke-linecap: round; stroke-linejoin: round; }

/* Yeni adım göründüğünde küçük bir giriş hareketi */
.pop { animation: pop 0.45s ease-out; }
.cursor { animation: nudge 1.8s ease-in-out infinite; }

@keyframes pop {
  from { opacity: 0; transform: scale(0.82); transform-origin: center; }
  to { opacity: 1; transform: scale(1); }
}

@keyframes nudge {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-6px, -4px); }
}

/* Hareketi azalt tercihi acikken animasyonlari kapat */
@media (prefers-reduced-motion: reduce) {
  .pop, .cursor { animation: none; }
  .progress-fill { transition: none; }
}

@media screen and (max-width: 1023px) {
  .intro-container { padding: 0 1rem; }
}

@media screen and (max-width: 768px) {
  .stage { padding: 1.25rem 1.25rem 0; }
  .stage-text { max-width: none; }
  .progress-rail { margin: 1.5rem -1.25rem 0; }
  .dot-label { display: none; }
  .step-dot { flex: 0 0 auto; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Intro.vue